import React from 'react';

import {
  Notifications as NotificationsIcon,
  Password as PasswordIcon,
  Person as PersonIcon,
  Phonelink as PhonelinkIcon,
  PrivacyTip as PrivacyIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';

import { appRoutes } from '@clearverify/constants';

import { DEFAULT_AVATAR } from 'src/assets';
import type { MenuItem } from 'src/components/common';
import { ItemDetailsLayout } from 'src/components/common';
import { useScreenInfo } from 'src/hooks/common';
import { useCurrentUser, useUserAvatar } from 'src/hooks/user';
import { SxProp } from 'src/types/mui';
import { getUserFullName } from 'src/utils/user';

export const AccountLayoutConsumer: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isMobile } = useScreenInfo();
  const { id: userId, user } = useCurrentUser();
  const { userAvatar, userAvatarLoading, updateUserAvatar, userAvatarUpdating } =
    useUserAvatar(userId);

  const fullName = getUserFullName(user);
  const title = user?.title;

  if (isMobile) {
    return <Box sx={mobileContentWrapperStyles}>{children}</Box>;
  }

  return (
    <ItemDetailsLayout
      name={fullName}
      subtitle={title}
      logoUrl={userAvatar?.downloadUrl || DEFAULT_AVATAR}
      menuItems={menuItems}
      logoProps={{
        onChange: updateUserAvatar,
        loading: userAvatarLoading || userAvatarUpdating,
      }}
    >
      <Box>{children}</Box>
    </ItemDetailsLayout>
  );
};

const menuItems: Array<MenuItem> = [
  {
    link: appRoutes.account.personalInformation,
    title: 'Personal Information',
    icon: PersonIcon,
  },
  {
    link: appRoutes.account.privacySettings,
    title: 'Privacy Settings',
    icon: PrivacyIcon,
  },
  {
    link: appRoutes.account.notifications,
    title: 'Notifications',
    icon: NotificationsIcon,
    divider: true,
  },
  {
    link: appRoutes.account.security,
    title: 'Sign-in & Security',
    icon: PasswordIcon,
  },
  {
    link: appRoutes.account.setupMfa,
    title: 'Multi-factor Authentication',
    icon: PhonelinkIcon,
  },
];

const mobileContentWrapperStyles: SxProp = {
  zIndex: '1',
  position: 'relative',
  height: '100%',
  width: '100%',
  overflowY: 'scroll',
  p: 3,
};
